import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Enums from '../Enums';
import { isNotNullOrUndefined } from '../../Util/TypeGuards';

export interface IMeasurementEntityAttributes extends IModelAttributes {
	label: string;
	value: number;
	measurementType: Enums.measurementType;
	showInVehicleDropdown: boolean;
	showInTrailerDropdown: boolean;

	cargoHeights: Array<Models.CargoTypeEntity | Models.ICargoTypeEntityAttributes>;
	cargoLengths: Array<Models.CargoTypeEntity | Models.ICargoTypeEntityAttributes>;
	cargoWeights: Array<Models.CargoTypeEntity | Models.ICargoTypeEntityAttributes>;
	prices: Array<Models.PriceEntity | Models.IPriceEntityAttributes>;
	trailerPrices: Array<Models.PriceEntity | Models.IPriceEntityAttributes>;
	selectedCargoHeights: Array<Models.CargoEntity | Models.ICargoEntityAttributes>;
	selectedCargoLengths: Array<Models.CargoEntity | Models.ICargoEntityAttributes>;
	selectedCargoWeights: Array<Models.CargoEntity | Models.ICargoEntityAttributes>;
	towOnHeights: Array<Models.TowOnEntity | Models.ITowOnEntityAttributes>;
	towOnLengths: Array<Models.TowOnEntity | Models.ITowOnEntityAttributes>;
	towOnTypeHeights: Array<Models.TowOnTypeEntity | Models.ITowOnTypeEntityAttributes>;
	towOnTypeLengths: Array<Models.TowOnTypeEntity | Models.ITowOnTypeEntityAttributes>;
	towOnTypeWeights: Array<Models.TowOnTypeEntity | Models.ITowOnTypeEntityAttributes>;
	towOnWeights: Array<Models.TowOnEntity | Models.ITowOnEntityAttributes>;
	capacityTypes: Array<Models.CapacityTypeMeasurement | Models.ICapacityTypeMeasurementAttributes>;
	configGenerals: Array<Models.ConfigGeneralEntity | Models.IConfigGeneralEntityAttributes>;
}
@entity('MeasurementEntity', 'Measurement')
export default class MeasurementEntity extends Model implements IMeasurementEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<MeasurementEntity, string>()
	public label: string;

	/**
	 * Value refers to the length or the weight of a vehicle or trailer
	 */
	@observable
	@attribute<MeasurementEntity, number>()
	public value: number;

	@observable
	@attribute<MeasurementEntity, Enums.measurementType>()
	public measurementType: Enums.measurementType;

	/**
	 * The minimum height required for this cargo item.
	 */
	@observable
	@attribute({ isReference: true, manyReference: true })
	public cargoHeights: Models.CargoTypeEntity[] = [];

	/**
	 * The minimum length required for this cargo item.
	 */
	@observable
	@attribute({ isReference: true, manyReference: true })
	public cargoLengths: Models.CargoTypeEntity[] = [];

	/**
	 * The minimum weight required for this cargo item.
	 */
	@observable
	@attribute({ isReference: true, manyReference: true })
	public cargoWeights: Models.CargoTypeEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public prices: Models.PriceEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public trailerPrices: Models.PriceEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public selectedCargoHeights: Models.CargoEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public selectedCargoLengths: Models.CargoEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public selectedCargoWeights: Models.CargoEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public towOnHeights: Models.TowOnEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public towOnLengths: Models.TowOnEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public towOnTypeHeights: Models.TowOnTypeEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public towOnTypeLengths: Models.TowOnTypeEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public towOnTypeWeights: Models.TowOnTypeEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public towOnWeights: Models.TowOnEntity[] = [];

	@observable
	@attribute<MeasurementEntity, boolean>()
	public showInVehicleDropdown: boolean;

	@observable
	@attribute<MeasurementEntity, boolean>()
	public showInTrailerDropdown: boolean;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public capacityTypes: Models.CapacityTypeMeasurement[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public configGenerals: Models.ConfigGeneralEntity[] = [];

	constructor(attributes?: Partial<IMeasurementEntityAttributes>) {
		super(attributes);

		this.cargoHeights = [];
		this.cargoLengths = [];
		this.cargoWeights = [];
		this.prices = [];
		this.trailerPrices = [];
		this.selectedCargoHeights = [];
		this.selectedCargoLengths = [];
		this.selectedCargoWeights = [];
		this.towOnHeights = [];
		this.towOnLengths = [];
		this.towOnTypeHeights = [];
		this.towOnTypeLengths = [];
		this.towOnTypeWeights = [];
		this.towOnWeights = [];
		this.capacityTypes = [];
		this.configGenerals = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IMeasurementEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.label !== undefined) {
				this.label = attributes.label;
			}
			if (attributes.value !== undefined) {
				this.value = attributes.value;
			}
			if (attributes.measurementType !== undefined) {
				this.measurementType = attributes.measurementType;
			}
			if (attributes.showInVehicleDropdown !== undefined) {
				this.showInVehicleDropdown = attributes.showInVehicleDropdown;
			}
			if (attributes.showInTrailerDropdown !== undefined) {
				this.showInTrailerDropdown = attributes.showInTrailerDropdown;
			}
			if (attributes.cargoHeights !== undefined && Array.isArray(attributes.cargoHeights)) {
				if (!this.cargoHeights) {
					this.cargoHeights = [];
				}
				for (const model of attributes.cargoHeights) {
					if (model instanceof Models.CargoTypeEntity) {
						this.cargoHeights.push(model);
					} else {
						this.cargoHeights.push(new Models.CargoTypeEntity(model));
					}
				}
			}
			if (attributes.cargoLengths !== undefined && Array.isArray(attributes.cargoLengths)) {
				if (!this.cargoLengths) {
					this.cargoLengths = [];
				}
				for (const model of attributes.cargoLengths) {
					if (model instanceof Models.CargoTypeEntity) {
						this.cargoLengths.push(model);
					} else {
						this.cargoLengths.push(new Models.CargoTypeEntity(model));
					}
				}
			}
			if (attributes.cargoWeights !== undefined && Array.isArray(attributes.cargoWeights)) {
				if (!this.cargoWeights) {
					this.cargoWeights = [];
				}
				for (const model of attributes.cargoWeights) {
					if (model instanceof Models.CargoTypeEntity) {
						this.cargoWeights.push(model);
					} else {
						this.cargoWeights.push(new Models.CargoTypeEntity(model));
					}
				}
			}
			if (attributes.prices !== undefined && Array.isArray(attributes.prices)) {
				if (!this.prices) {
					this.prices = [];
				}
				for (const model of attributes.prices) {
					if (model instanceof Models.PriceEntity) {
						this.prices.push(model);
					} else {
						this.prices.push(new Models.PriceEntity(model));
					}
				}
			}
			if (attributes.trailerPrices !== undefined && Array.isArray(attributes.trailerPrices)) {
				if (!this.trailerPrices) {
					this.trailerPrices = [];
				}
				for (const model of attributes.trailerPrices) {
					if (model instanceof Models.PriceEntity) {
						this.trailerPrices.push(model);
					} else {
						this.trailerPrices.push(new Models.PriceEntity(model));
					}
				}
			}
			if (attributes.selectedCargoHeights !== undefined && Array.isArray(attributes.selectedCargoHeights)) {
				if (!this.selectedCargoHeights) {
					this.selectedCargoHeights = [];
				}
				for (const model of attributes.selectedCargoHeights) {
					if (model instanceof Models.CargoEntity) {
						this.selectedCargoHeights.push(model);
					} else {
						this.selectedCargoHeights.push(new Models.CargoEntity(model));
					}
				}
			}
			if (attributes.selectedCargoLengths !== undefined && Array.isArray(attributes.selectedCargoLengths)) {
				if (!this.selectedCargoLengths) {
					this.selectedCargoLengths = [];
				}
				for (const model of attributes.selectedCargoLengths) {
					if (model instanceof Models.CargoEntity) {
						this.selectedCargoLengths.push(model);
					} else {
						this.selectedCargoLengths.push(new Models.CargoEntity(model));
					}
				}
			}
			if (attributes.selectedCargoWeights !== undefined && Array.isArray(attributes.selectedCargoWeights)) {
				if (!this.selectedCargoWeights) {
					this.selectedCargoWeights = [];
				}
				for (const model of attributes.selectedCargoWeights) {
					if (model instanceof Models.CargoEntity) {
						this.selectedCargoWeights.push(model);
					} else {
						this.selectedCargoWeights.push(new Models.CargoEntity(model));
					}
				}
			}
			if (attributes.towOnHeights !== undefined && Array.isArray(attributes.towOnHeights)) {
				if (!this.towOnHeights) {
					this.towOnHeights = [];
				}
				for (const model of attributes.towOnHeights) {
					if (model instanceof Models.TowOnEntity) {
						this.towOnHeights.push(model);
					} else {
						this.towOnHeights.push(new Models.TowOnEntity(model));
					}
				}
			}
			if (attributes.towOnLengths !== undefined && Array.isArray(attributes.towOnLengths)) {
				if (!this.towOnLengths) {
					this.towOnLengths = [];
				}
				for (const model of attributes.towOnLengths) {
					if (model instanceof Models.TowOnEntity) {
						this.towOnLengths.push(model);
					} else {
						this.towOnLengths.push(new Models.TowOnEntity(model));
					}
				}
			}
			if (attributes.towOnTypeHeights !== undefined && Array.isArray(attributes.towOnTypeHeights)) {
				if (!this.towOnTypeHeights) {
					this.towOnTypeHeights = [];
				}
				for (const model of attributes.towOnTypeHeights) {
					if (model instanceof Models.TowOnTypeEntity) {
						this.towOnTypeHeights.push(model);
					} else {
						this.towOnTypeHeights.push(new Models.TowOnTypeEntity(model));
					}
				}
			}
			if (attributes.towOnTypeLengths !== undefined && Array.isArray(attributes.towOnTypeLengths)) {
				if (!this.towOnTypeLengths) {
					this.towOnTypeLengths = [];
				}
				for (const model of attributes.towOnTypeLengths) {
					if (model instanceof Models.TowOnTypeEntity) {
						this.towOnTypeLengths.push(model);
					} else {
						this.towOnTypeLengths.push(new Models.TowOnTypeEntity(model));
					}
				}
			}
			if (attributes.towOnTypeWeights !== undefined && Array.isArray(attributes.towOnTypeWeights)) {
				if (!this.towOnTypeWeights) {
					this.towOnTypeWeights = [];
				}
				for (const model of attributes.towOnTypeWeights) {
					if (model instanceof Models.TowOnTypeEntity) {
						this.towOnTypeWeights.push(model);
					} else {
						this.towOnTypeWeights.push(new Models.TowOnTypeEntity(model));
					}
				}
			}
			if (attributes.towOnWeights !== undefined && Array.isArray(attributes.towOnWeights)) {
				if (!this.towOnWeights) {
					this.towOnWeights = [];
				}
				for (const model of attributes.towOnWeights) {
					if (model instanceof Models.TowOnEntity) {
						this.towOnWeights.push(model);
					} else {
						this.towOnWeights.push(new Models.TowOnEntity(model));
					}
				}
			}
			if (isNotNullOrUndefined(attributes.capacityTypes)) {
				for (const model of attributes.capacityTypes) {
					if (model instanceof Models.CapacityTypeMeasurement) {
						this.capacityTypes.push(model);
					} else {
						this.capacityTypes.push(new Models.CapacityTypeMeasurement(model));
					}
				}
			}
			if (attributes.configGenerals !== undefined && Array.isArray(attributes.configGenerals)) {
				if (!this.configGenerals) {
					this.configGenerals = [];
				}
				for (const model of attributes.configGenerals) {
					if (model instanceof Models.ConfigGeneralEntity) {
						this.configGenerals.push(model);
					} else {
						this.configGenerals.push(new Models.ConfigGeneralEntity(model));
					}
				}
			}
		}
	}
}
